import { useFormContext } from "react-hook-form";

import { ControlledIntegerInput } from "@krea/shared/design-system/ControlledInputs/IntegerInput";
import { MoneyInput } from "@krea/shared/design-system/ControlledInputs/MoneyInput";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@krea/shared/design-system/Form";
import {
  RadioGroup,
  RadioGroupItem,
} from "@krea/shared/design-system/RadioGroup";
import type { Currency } from "@krea/shared/types/common";

import { LeasingBidTypeEnum } from "@/features/common/enums";
import type { ILeasingBidInput } from "@/features/lender-responses/types/lender-response";
import type { BidPostData } from "@/validations";

import { useFormatCurrency } from "../../../../../utils/customHooks/useFormatCurrency";
import DetailsItem from "../../../../details/item";

function TypeOfContractRadioGroup({
  value,
  onChange,
  isSubmitting,
}: {
  value: string;
  onChange: (v: string) => void;
  isSubmitting: boolean;
}) {
  const { formItemId } = useFormField();
  const leasingId = formItemId;
  const rentId = `${formItemId}-rent`;

  return (
    <RadioGroup
      onValueChange={onChange}
      value={value}
      disabled={isSubmitting}
      className="flex flex-row gap-4"
    >
      <FormItem className="flex items-center gap-2 space-y-0">
        <FormControl>
          <RadioGroupItem value={LeasingBidTypeEnum.LEASING} id={leasingId} />
        </FormControl>
        <FormLabel
          htmlFor={leasingId}
          className="cursor-pointer font-normal"
        >
          Leasing
        </FormLabel>
      </FormItem>
      <FormItem className="flex items-center gap-2 space-y-0">
        <FormControl>
          <RadioGroupItem value={LeasingBidTypeEnum.RENT} id={rentId} />
        </FormControl>
        <FormLabel
          htmlFor={rentId}
          className="cursor-pointer font-normal"
        >
          Rent
        </FormLabel>
      </FormItem>
    </RadioGroup>
  );
}

interface IFormProps {
  bidFormIndex: number;
  currency: Currency;
  isEditing: boolean; // Is editing existing offer or creating new
  isInterestRatePerYearActivated?: boolean;
}

export const LeasingForm = ({
  bidFormIndex,
  currency,
  isEditing,
}: IFormProps) => {
  const [, calculatedCurrency] = useFormatCurrency({
    currency,
  });

  const {
    control,
    formState: { isSubmitting },
  } = useFormContext<BidPostData<ILeasingBidInput>>();

  return (
    <div className="flex w-full flex-col">
      <div className="w-full">
        <DetailsItem
          title={isEditing ? "Update leasing terms" : "Leasing terms"}
          className="mb-0"
        >
          <div className="w-full space-y-4">
            <MoneyInput
              name={`bids.${bidFormIndex}.amount`}
              label="Amount"
              control={control}
              currency={calculatedCurrency!}
              required
            />

            <MoneyInput
              name={`bids.${bidFormIndex}.downPayment`}
              control={control}
              label="Down payment"
              currency={calculatedCurrency!}
              required
            />

            <ControlledIntegerInput
              control={control}
              name={`bids.${bidFormIndex}.paybackTimeMonths`}
              label="Repayment time"
              required
              rightText="months"
              acceptsDecimals={false}
            />

            <MoneyInput
              control={control}
              name={`bids.${bidFormIndex}.monthlyPayment`}
              label="Monthly payment"
              currency={calculatedCurrency!}
              required
            />

            <ControlledIntegerInput
              control={control}
              name={`bids.${bidFormIndex}.terminalValuePercentage`}
              label="Terminal value"
              required
              rightText="%"
              placeholder='Value in "%"'
            />

            <FormField
              control={control}
              name={`bids.${bidFormIndex}.contractType`}
              render={({ field: { value, onChange } }) => {
                const radioValue = value ?? "";

                return (
                  <FormItem>
                    <FormLabel className="font-bold">
                      Type of contract{" "}
                      <span className="text-red-300">*</span>
                    </FormLabel>
                    <FormControl>
                      <TypeOfContractRadioGroup
                        value={radioValue}
                        onChange={onChange}
                        isSubmitting={isSubmitting}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <MoneyInput
              control={control}
              name={`bids.${bidFormIndex}.setupFee`}
              label="Setup fee"
              currency={calculatedCurrency!}
            />
          </div>
        </DetailsItem>

        <DetailsItem title="">
          <div className="mt-8 w-full">
            <span className="text-sm italic text-gray-600">
              Here you can explain other costs & qualitative information about
              your offer:
            </span>
          </div>

          <div className="w-full">
            <MoneyInput
              control={control}
              name={`bids.${bidFormIndex}.insuranceMonthlyCost`}
              label="Insurance cost (per month)"
              currency={calculatedCurrency!}
            />

            <MoneyInput
              control={control}
              name={`bids.${bidFormIndex}.postalFee`}
              label="Postal fee (per month)"
              currency={calculatedCurrency!}
            />
          </div>
        </DetailsItem>
      </div>
    </div>
  );
};
